// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0

/**
 * F5 — client-side image preparation for Amy photo attachments.
 *
 * Downscales to ≤1280px on the longest edge, re-encodes as JPEG q0.8
 * (PNG sources are flattened onto white — JPEG has no alpha), and rejects
 * anything still oversize after compression with a typed error the caller
 * maps to a localized message. The agent enforces the same caps
 * server-side (patterns/bitebalance-agent/tools/images.py) — this is UX,
 * the server is the trust boundary.
 *
 * Kept as a standalone module so the canvas/bitmap work is mockable in
 * jsdom component tests (no real image decoding there).
 */

export const MAX_EDGE_PX = 1280
export const JPEG_QUALITY = 0.8
/** Reject after compression above this (brief: 4MB binary). */
export const MAX_BLOB_BYTES = 4 * 1024 * 1024
/** AgentCore invoke payloads must stay small: ≤ ~3MB base64 total. */
export const MAX_BASE64_BYTES = 3 * 1024 * 1024

export interface ProcessedImage {
  /** Raw base64 (no data: prefix), always re-encoded JPEG. */
  base64: string
  format: "jpeg"
  /** Object URL of the compressed blob, for the thumbnail + chat bubble. */
  previewUrl: string
}

export interface ImageProcessFailure {
  error: "tooLarge" | "readError"
}

async function decodeToBitmap(file: File): Promise<ImageBitmap | HTMLImageElement> {
  if (typeof createImageBitmap === "function") {
    return createImageBitmap(file)
  }
  // Older-Safari fallback: decode through an <img> element.
  const url = URL.createObjectURL(file)
  try {
    const image = new Image()
    image.src = url
    await image.decode()
    return image
  } finally {
    URL.revokeObjectURL(url)
  }
}

function toBlob(canvas: HTMLCanvasElement): Promise<Blob | null> {
  return new Promise(resolve => canvas.toBlob(resolve, "image/jpeg", JPEG_QUALITY))
}

function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => {
      const dataUrl = String(reader.result)
      resolve(dataUrl.slice(dataUrl.indexOf(",") + 1))
    }
    reader.onerror = () => reject(reader.error)
    reader.readAsDataURL(blob)
  })
}

export async function processImageFile(file: File): Promise<ProcessedImage | ImageProcessFailure> {
  let bitmap: ImageBitmap | HTMLImageElement
  try {
    bitmap = await decodeToBitmap(file)
  } catch {
    return { error: "readError" }
  }

  const sourceWidth = "naturalWidth" in bitmap ? bitmap.naturalWidth : bitmap.width
  const sourceHeight = "naturalHeight" in bitmap ? bitmap.naturalHeight : bitmap.height
  if (!sourceWidth || !sourceHeight) return { error: "readError" }

  const scale = Math.min(1, MAX_EDGE_PX / Math.max(sourceWidth, sourceHeight))
  const canvas = document.createElement("canvas")
  canvas.width = Math.max(1, Math.round(sourceWidth * scale))
  canvas.height = Math.max(1, Math.round(sourceHeight * scale))
  const ctx = canvas.getContext("2d")
  if (!ctx) return { error: "readError" }
  // JPEG has no transparency — flatten PNG alpha onto white, not black.
  ctx.fillStyle = "#ffffff"
  ctx.fillRect(0, 0, canvas.width, canvas.height)
  ctx.drawImage(bitmap, 0, 0, canvas.width, canvas.height)
  if ("close" in bitmap) bitmap.close()

  const blob = await toBlob(canvas)
  if (!blob) return { error: "readError" }
  if (blob.size > MAX_BLOB_BYTES) return { error: "tooLarge" }

  let base64: string
  try {
    base64 = await blobToBase64(blob)
  } catch {
    return { error: "readError" }
  }
  if (base64.length > MAX_BASE64_BYTES) return { error: "tooLarge" }

  return { base64, format: "jpeg", previewUrl: URL.createObjectURL(blob) }
}
